import { useQuery } from '@tanstack/react-query';
import { loyaltyActivatedCouponStorageKey } from 'pi-kiosk-shared/loyalty/loyaltySessionHandoff';
import { clearPromoActivatedRewardHandoff } from 'pi-kiosk-shared/promo/promoSessionHandoff';
import { useApiClient } from '../../../shared/providers/AppDataProvider.js';
import { useTenantCode } from '../../../shared/tenant/TenantProvider.js';
import { hasCustomerApiTenant } from '../../../shared/tenant/customerApiTenant.js';
import { customerQueryKeys } from '../../../shared/api/queryKeys.js';
import type { PromoPreviewPricingResultDTO } from '../../promotions/promoRewardsApi.js';
import { readActivatedPromoRewardHandoff } from '../../promotions/readActivatedPromoRewardHandoff.js';

export interface CheckoutPreviewLineInput {
  readonly productId: number;
  readonly variantId: number | null;
  readonly quantity: number;
  readonly unitPrice: number;
  readonly lineKey: string;
}

export interface LoyaltyPreviewPricingResult {
  readonly cartDiscountAmount: number;
  readonly estimatedEarnPoints: number;
}

export interface CheckoutPricingPreviewResult {
  readonly promo: PromoPreviewPricingResultDTO | null;
  readonly loyalty: LoyaltyPreviewPricingResult | null;
  readonly stackingMode: 'EXCLUSIVE' | 'STACK_PROMO_THEN_LOYALTY';
  readonly mutualExclusion: boolean;
  readonly totalDiscountAmount: number;
  /** §3.9 RELEASE observed — callers should clearApplied chip. */
  readonly promoClaimReleased: boolean;
}

function readActivatedCouponHandoff(
  tenantCode: string,
  checkoutSessionId: string | null | undefined,
): string | null {
  if (checkoutSessionId == null || checkoutSessionId.trim().length === 0) {
    return null;
  }
  if (typeof sessionStorage === 'undefined') {
    return null;
  }
  try {
    const value = sessionStorage.getItem(
      loyaltyActivatedCouponStorageKey(tenantCode, checkoutSessionId),
    );
    return value != null && value.length > 0 ? value : null;
  } catch {
    return null;
  }
}

type PreviewSyncPayload = PromoPreviewPricingResultDTO & {
  readonly syncAction?: 'noop' | 'updated' | 'released' | string;
};

function detectPromoClaimReleased(input: {
  readonly sentRewardId: string | null;
  readonly promo: PreviewSyncPayload | null;
}): boolean {
  if (input.sentRewardId == null || input.sentRewardId.length === 0 || input.promo == null) {
    return false;
  }
  if (input.promo.syncAction === 'released') {
    return true;
  }
  if (input.promo.syncAction === 'updated' || input.promo.syncAction === 'noop') {
    return false;
  }
  // RELEASE re-previews with activatedPromoRewardId null → empty MANUAL surface.
  return input.promo.cartDiscountAmount === 0 && input.promo.adjustments.length === 0;
}

function resolvePreviewActivatedPromoRewardId(input: {
  readonly serverRewardId: string | null | undefined;
  readonly activatedPromoRewardId: string | null | undefined;
  readonly tenantCode: string;
  readonly checkoutSessionId: string | null;
}): string | null {
  if (input.serverRewardId === null) {
    return null;
  }
  if (input.activatedPromoRewardId !== undefined) {
    return input.activatedPromoRewardId;
  }
  if (input.serverRewardId !== undefined) {
    return input.serverRewardId;
  }
  return readActivatedPromoRewardHandoff(input.tenantCode, input.checkoutSessionId);
}

function resolvePreviewActivatedCouponId(input: {
  readonly activatedCouponId: string | null | undefined;
  readonly tenantCode: string;
  readonly checkoutSessionId: string | null;
}): string | null {
  if (input.activatedCouponId !== undefined) {
    return input.activatedCouponId;
  }
  return readActivatedCouponHandoff(input.tenantCode, input.checkoutSessionId);
}

function promoResultAfterClaimRelease(
  promo: PromoPreviewPricingResultDTO | null,
  promoClaimReleased: boolean,
): PromoPreviewPricingResultDTO | null {
  if (!promoClaimReleased) {
    return promo;
  }
  if (promo == null) {
    return null;
  }
  return { ...promo, cartDiscountAmount: 0, adjustments: [] };
}

export function useCheckoutPricingPreview(input: {
  readonly customerId: number | null;
  readonly checkoutSessionId: string | null;
  readonly items: readonly CheckoutPreviewLineInput[];
  readonly promotionsEnabled: boolean;
  readonly loyaltyEnabled: boolean;
  readonly activatedPromoRewardId?: string | null;
  readonly activatedCouponId?: string | null;
  /** §3.8 server session SoT when checkout session was loaded. */
  readonly serverActivatedPromoRewardId?: string | null;
  readonly serverPromoResolved?: boolean;
}) {
  const api = useApiClient();
  const tenantCode = useTenantCode();
  const serverRewardId =
    input.serverPromoResolved === true
      ? (input.serverActivatedPromoRewardId?.trim() || null)
      : undefined;
  // §3.8: server null after RELEASE always wins. Otherwise prefer caller
  // (exclusive conflict may force null) over handoff.
  const activatedPromoRewardId = resolvePreviewActivatedPromoRewardId({
    serverRewardId,
    activatedPromoRewardId: input.activatedPromoRewardId,
    tenantCode,
    checkoutSessionId: input.checkoutSessionId,
  });
  const activatedCouponId = resolvePreviewActivatedCouponId({
    activatedCouponId: input.activatedCouponId,
    tenantCode,
    checkoutSessionId: input.checkoutSessionId,
  });

  return useQuery({
    queryKey: customerQueryKeys.checkoutPricingPreview(tenantCode, {
      customerId: input.customerId ?? 0,
      sessionId: input.checkoutSessionId ?? '',
      items: input.items,
      activatedPromoRewardId,
      activatedCouponId,
    }),
    enabled:
      hasCustomerApiTenant(tenantCode) &&
      input.items.length > 0 &&
      input.checkoutSessionId != null &&
      input.checkoutSessionId.length > 0 &&
      ((input.promotionsEnabled && input.customerId != null && input.customerId > 0) ||
        (input.loyaltyEnabled && input.customerId != null && input.customerId > 0)),
    queryFn: async (): Promise<CheckoutPricingPreviewResult> => {
      // Belt-and-suspenders: never hit ApiClient under platform chrome / reserved paths.
      if (!hasCustomerApiTenant(tenantCode)) {
        return {
          promo: null,
          loyalty: null,
          stackingMode: 'EXCLUSIVE',
          mutualExclusion: false,
          totalDiscountAmount: 0,
          promoClaimReleased: false,
        };
      }

      const customerId = input.customerId;
      let promo: PromoPreviewPricingResultDTO | null = null;
      let loyalty: LoyaltyPreviewPricingResult | null = null;
      let promoClaimReleased = false;

      if (input.promotionsEnabled && customerId != null && customerId > 0) {
        const promoResponse = await api.request<PreviewSyncPayload>(
          '/promotions/preview-pricing',
          {
            method: 'POST',
            body: {
              customerId,
              channel: 'CUSTOMER_CHECKOUT',
              items: input.items,
              activatedPromoRewardId,
              ...(input.checkoutSessionId
                ? { checkoutSessionPublicId: input.checkoutSessionId }
                : {}),
            },
          },
        );
        promo = promoResponse;
        promoClaimReleased = detectPromoClaimReleased({
          sentRewardId: activatedPromoRewardId,
          promo: promoResponse,
        });

        if (!promoClaimReleased && activatedPromoRewardId != null && input.checkoutSessionId) {
          try {
            const session = await api.request<{
              promotions?: { activatedPromoRewardId?: string | null };
            }>(`/customer-checkout/sessions/${encodeURIComponent(input.checkoutSessionId)}`);
            const serverReward = session.promotions?.activatedPromoRewardId?.trim() ?? '';
            if (serverReward.length === 0) {
              promoClaimReleased = true;
            }
          } catch {
            // Best-effort — keep preview result; handoff clear waits for next session read.
          }
        }

        if (promoClaimReleased && input.checkoutSessionId) {
          clearPromoActivatedRewardHandoff(tenantCode, input.checkoutSessionId);
        }
      }

      if (input.loyaltyEnabled && customerId != null && customerId > 0) {
        const loyaltyResponse = await api.request<{
          pricingDecision?: { cartDiscountAmount?: number } | null;
          estimatedEarnPoints?: number;
        }>('/loyalty/preview-pricing', {
          method: 'POST',
          body: {
            customerId,
            items: input.items,
            activatedCouponId,
            paymentId: `checkout-preview-${input.checkoutSessionId ?? 'unknown'}`,
          },
        });
        loyalty = {
          cartDiscountAmount: loyaltyResponse.pricingDecision?.cartDiscountAmount ?? 0,
          estimatedEarnPoints: loyaltyResponse.estimatedEarnPoints ?? 0,
        };
      }

      const stackingMode = promo?.stackingMode ?? 'EXCLUSIVE';
      const effectivePromoId = promoClaimReleased ? null : activatedPromoRewardId;
      const mutualExclusion =
        stackingMode === 'EXCLUSIVE' &&
        effectivePromoId != null &&
        activatedCouponId != null;
      const promoDiscount = promoClaimReleased ? 0 : (promo?.cartDiscountAmount ?? 0);
      const loyaltyDiscount = loyalty?.cartDiscountAmount ?? 0;
      const totalDiscountAmount =
        stackingMode === 'STACK_PROMO_THEN_LOYALTY'
          ? promoDiscount + loyaltyDiscount
          : Math.max(promoDiscount, loyaltyDiscount);

      return {
        promo: promoResultAfterClaimRelease(promo, promoClaimReleased),
        loyalty,
        stackingMode,
        mutualExclusion,
        totalDiscountAmount,
        promoClaimReleased,
      };
    },
  });
}
